"use client";

import { cn } from "@/lib/utils";
import type { LoaderProps } from "@/types";

const Loader = ({ 
  size = "md", 
  variant = "spinner", 
  color = "primary",
  customColor,
  className = "",
  text
}: LoaderProps) => {
  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "h-3 w-3";
      case "md":
        return "h-4 w-4";
      case "lg":
        return "h-6 w-6";
      case "xl":
        return "h-8 w-8";
      default:
        return "h-4 w-4";
    }
  };

  const getColorClasses = () => {
    if (customColor) {
      return "";
    }
    
    switch (color) {
      case "primary":
        return "text-brand-red";
      case "secondary":
        return "text-brand-blue";
      case "white":
        return "text-white";
      case "gray":
        return "text-gray-500";
      case "brand-red":
        return "text-brand-red";
      case "brand-blue":
        return "text-brand-blue";
      default:
        return "text-brand-red";
    }
  };

  const Spinner = () => (
    <svg
      className={cn("animate-spin", getSizeClasses(), getColorClasses())}
      style={customColor ? { color: customColor } : undefined}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const Dots = () => (
    <div className="flex space-x-1">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            "rounded-full bg-current animate-pulse",
            getSizeClasses(),
            getColorClasses()
          )}
          style={{
            animationDelay: `${i * 0.2}s`,
            animationDuration: "1s",
            ...(customColor && { backgroundColor: customColor })
          }}
        />
      ))}
    </div>
  );

  const Pulse = () => (
    <div
      className={cn(
        "rounded-full animate-pulse",
        getSizeClasses(),
        getColorClasses(),
        "bg-current"
      )}
      style={customColor ? { backgroundColor: customColor } : undefined}
    />
  );

  const renderLoader = () => {
    switch (variant) {
      case "spinner":
        return <Spinner />;
      case "dots":
        return <Dots />;
      case "pulse":
        return <Pulse />;
      default:
        return <Spinner />;
    }
  };

  return (
    <div className={cn("flex items-center justify-center", className)}>
      <div className="flex items-center gap-2">
        {renderLoader()}
        {text && (
          <span 
            className={cn("text-sm font-medium", getColorClasses())}
            style={customColor ? { color: customColor } : undefined}
          >
            {text}
          </span>
        )}
      </div>
    </div>
  );
};

export default Loader;
